/**
 * Extracts the base layer ID from a given layer ID string.
 * The base layer ID is the substring before the first underscore.
 * e.g., "h173_l0_v3" becomes "h173"
 * @param {string} layerId - The full layer ID string (e.g., "hazard_123").
 * @returns {string} The base layer ID, or the original string if no underscore is present.
 */
export const getBaseLayerId = (layerId: string): string => {
  if (!layerId) return layerId
  return layerId.split('_')[0]
}

import { Polygon } from 'geojson'
import type { MapLayer, DrawArea } from '@app/types/layerTypes'
import { MultiRiskDataObject, RiskData } from '../types/layers.types'
import { VisibleAssets } from '@app/components/Molecules/MapView/RiskMapView/RiskMapView.utilities'
import apiClient from '@app/config/axios.config'
import {
  HazardCatalog,
  HazardDefinition,
  HazardScenario,
} from '@app/domains/hazards/types/hazard.types'
import type {
  ContextualCatalog,
  ContextualCategory,
  ContextualGroup,
  ContextualLayer,
  ElementCatalog,
  ElementCategory,
  ElementGroup,
  ElementItem,
} from '@app/domains/layers/types/layers.types'

export function flattenElementsFromCatalog(catalog: ElementCatalog): Array<{
  categoryKey: string
  category: ElementCategory
  group: ElementGroup
  asset: ElementItem
}> {
  const out: Array<{
    categoryKey: string
    category: ElementCategory
    group: ElementGroup
    asset: ElementItem
  }> = []
  for (const [categoryKey, category] of Object.entries(catalog)) {
    for (const group of category.groups ?? []) {
      for (const asset of group.assets ?? []) {
        out.push({ categoryKey, category, group, asset })
      }
    }
  }
  return out
}

export function flattenContextualFromCatalog(catalog: ContextualCatalog): Array<{
  categoryKey: string
  category: ContextualCategory
  group: ContextualGroup
  layer: ContextualLayer
}> {
  const out: Array<{
    categoryKey: string
    category: ContextualCategory
    group: ContextualGroup
    layer: ContextualLayer
  }> = []
  for (const [categoryKey, category] of Object.entries(catalog)) {
    for (const group of category.groups ?? []) {
      for (const layer of group.layers ?? []) {
        out.push({ categoryKey, category, group, layer })
      }
    }
  }
  return out
}

export function flattenHazardsFromCatalog(
  hazards: HazardCatalog,
): Array<{ hazard: HazardDefinition; scenario: HazardScenario }> {
  const out: Array<{ hazard: HazardDefinition; scenario: HazardScenario }> = []
  for (const hazard of hazards ?? []) {
    for (const scenario of hazard.scenarios ?? []) {
      out.push({ hazard, scenario })
    }
  }
  return out
}

export const fetchIdentifyingInformationAssetExposureStats = async (
  assetLayers: MapLayer[],
  hazardLayers: MapLayer[],
  attribute: string,
  regions: string[],
  drawAreas: DrawArea[],
  visibleAssets?: VisibleAssets,
  abortController?: AbortController,
): Promise<RiskData[]> => {
  const assetLayerTypes = assetLayers.map((assetLayer) => assetLayer.type)
  const hazardLayerAssetTags = hazardLayers.map((hazardLayer) => hazardLayer.assetTag)
  const query = [`attribute=${attribute}`]
  if (regions.length > 0) query.push(`regions=${regions.join(',')}`)
  const drawAreaCoordinateArray: Polygon | null =
    drawAreas.length > 0 ? (drawAreas[0].geometry as Polygon) : null
  const drawAreaCsv = drawAreaCoordinateArray
    ? drawAreaCoordinateArray.coordinates[0].flat().join(',')
    : ''

  const mappedVisibleAssets = visibleAssets
    ? Object.keys(visibleAssets).reduce(
        (acc, assetType) => {
          acc[assetType] = Object.keys(visibleAssets[assetType])
          return acc
        },
        {} as { [key: string]: string[] },
      )
    : visibleAssets

  const url = `/asset/exposure/identifying_information_stats/${assetLayerTypes.join(
    ',',
  )}/${hazardLayerAssetTags.join(',')}/${drawAreaCsv}?${query.join('&')}`

  const response = await apiClient.post<RiskData[]>(
    url,
    {
      prefiltered_asset_ids: mappedVisibleAssets,
    },
    { signal: abortController?.signal },
  )

  return response.data.map((data) => ({ ...data, identifying_information_property: attribute }))
}

export const fetchAssetExposureStats = async (
  assetLayers: MapLayer[],
  hazardLayers: MapLayer[],
  regions: string[],
  drawAreas: DrawArea[],
  visibleAssets?: VisibleAssets,
  abortController?: AbortController,
): Promise<RiskData[]> => {
  const assetLayerTypes = assetLayers.map((assetLayer) => assetLayer.type)
  const hazardLayerAssetTags = hazardLayers.map((hazardLayer) => hazardLayer.assetTag)
  const query = []
  if (regions.length > 0) query.push(`regions=${regions.join(',')}`)
  const drawAreaCoordinateArray: Polygon | null =
    drawAreas.length > 0 ? (drawAreas[0].geometry as Polygon) : null
  const drawAreaCsv = drawAreaCoordinateArray
    ? drawAreaCoordinateArray.coordinates[0].flat().join(',')
    : ''

  const mappedVisibleAssets = visibleAssets
    ? Object.keys(visibleAssets).reduce(
        (acc, assetType) => {
          acc[assetType] = Object.keys(visibleAssets[assetType])
          return acc
        },
        {} as { [key: string]: string[] },
      )
    : visibleAssets

  const url = `/asset/exposure/stats/${assetLayerTypes.join(',')}/${hazardLayerAssetTags.join(
    ',',
  )}/${drawAreaCsv}?${query.join('&')}`

  const response = await apiClient.post<RiskData[]>(
    url,
    {
      prefiltered_asset_ids: mappedVisibleAssets,
    },
    { signal: abortController?.signal },
  )

  return response.data
}

export const fetchAssetMultiHazardExposureStats = async (
  assetLayers: MapLayer[],
  hazardLayers: MapLayer[],
  regions: string[],
  drawAreas: DrawArea[],
  abortController: AbortController,
  visibleAssets?: VisibleAssets,
): Promise<MultiRiskDataObject> => {
  const assetLayerTypes = assetLayers.map((assetLayer) => assetLayer.type)
  const hazardLayerAssetTags = hazardLayers.map((hazardLayer) => hazardLayer.assetTag)
  const query = []
  if (regions.length > 0) query.push(`regions=${regions.join(',')}`)
  const drawAreaCoordinateArray: Polygon | null =
    drawAreas.length > 0 ? (drawAreas[0].geometry as Polygon) : null
  const drawAreaCsv = drawAreaCoordinateArray
    ? drawAreaCoordinateArray.coordinates[0].flat().join(',')
    : ''

  const mappedVisibleAssets = visibleAssets
    ? Object.keys(visibleAssets).reduce(
        (acc, assetType) => {
          acc[assetType] = Object.keys(visibleAssets[assetType])
          return acc
        },
        {} as { [key: string]: string[] },
      )
    : visibleAssets

  const url = `/asset/exposure/multihazard_stats/${assetLayerTypes.join(
    ',',
  )}/${hazardLayerAssetTags.join(',')}/${drawAreaCsv}?${query.join('&')}`

  const response = await apiClient.post<MultiRiskDataObject>(
    url,
    {
      prefiltered_asset_ids: mappedVisibleAssets,
    },
    { signal: abortController.signal },
  )

  return response.data
}

export const fetchCheckIfIdentifyingInformationExists = async (
  attribute: string,
  assetLayers: MapLayer[],
): Promise<{ asset_type: string; has_identifying_information: boolean; attribute: string }[]> => {
  if (assetLayers.length === 0) return []
  try {
    const assetLayerTypes = assetLayers.map((assetLayer) => assetLayer.type)

    const url = `/asset/check_identifying_information_exists/${assetLayerTypes.join(
      ',',
    )}/?attribute=${attribute}`

    const response =
      await apiClient.post<{ asset_type: string; has_identifying_information: boolean }[]>(url)

    return response.data.map((data) => ({ ...data, attribute }))
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return []
  }
}
